import { useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useConvertEventToBroadcast } from "@/react/components/EventsV3/Modal/hooks/useConvertEventToBroadcast";
import { useIsEventConnectionsEnabled } from "@/react/components/EventsV3/Modal/hooks/useIsEventConnectionsEnabled";
import { BadgeV2 } from "@/react/components/shared/uikit/BadgeV2";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import type { Event } from "@/react/types/Event";
import { DeleteRecurringModal } from "@circle-react/components/Events/DeleteRecurringModal";
import { DuplicateEventModal } from "@circle-react/components/Events/ListViewItem/EventMenu/DuplicateEventModal";
import { EVENT_TYPES } from "@circle-react/components/Events/helpers/constants";
import type { RecurringEditMode } from "@circle-react/components/Events/helpers/constants";
import { isPaidEvent } from "@circle-react/components/EventsV3/Modal/utils";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { usePunditUserContext } from "@circle-react/contexts";
import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { Icon } from "@circle-react-shared/Icon";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { PinEventToTopAction } from "./PinEventToTopAction";
import { useEventMenu } from "./hooks/useEventMenu";

interface EventMenuProps {
  event: Event;
  onDestroy: (eventId: number) => void;
}

export const EventMenu = ({ event, onDestroy }: EventMenuProps) => {
  const { event_type: eventType } = event;
  const { pathname } = useLocation();
  const isAllEventsPage = pathname === "/events";
  const { refetchPostDetails } = useCurrentPostHelpers();
  const { isViewOnlyMasquerading, currentCommunityMember } =
    usePunditUserContext();
  const isEventConnectionsEnabled = useIsEventConnectionsEnabled();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const isEventShareableViaBroadcast =
    isEventConnectionsEnabled && isAdmin && event.status !== "draft";

  const {
    canEdit,
    onEdit,
    canDelete,
    canDuplicate,
    onDelete,
    isDeleting,
    onDuplicate,
    onDeleteClickHandler,
    isDuplicateEventModalOpen,
    isConfirmationModalOpen,
    toggleConfirmationModal,
    isRecurringModalOpen,
    toggleRecurringModal,
  } = useEventMenu({ event, onDestroy });

  const { onSendAnnouncement, isLoading: isSendingAnnouncement } =
    useConvertEventToBroadcast();

  if (!canEdit && !canDelete) {
    return null;
  }

  if (isViewOnlyMasquerading) {
    return (
      <div className="cursor-not-allowed py-1">
        <Icon type="menu-dots" />
      </div>
    );
  }

  return (
    <>
      <Dropdown
        direction="bottom-end"
        button={
          <Dropdown.MenuButton>
            <div data-testid="action-more">
              <Icon type="menu-dots" />
            </div>
          </Dropdown.MenuButton>
        }
        size="large"
        transitionWrapperClassName="z-50"
        dataTestId="event-menu-dropdown"
      >
        <BookmarkButton
          variant="dropdown"
          bookmarkType="event"
          record={event}
          onSuccess={refetchPostDetails}
        />
        {canEdit && (
          <Dropdown.ItemLink onClick={onEdit} id="event-menu-edit" forceClose>
            {t("edit_event")}
          </Dropdown.ItemLink>
        )}
        {canDuplicate && (
          <Dropdown.ItemLink onClick={onDuplicate}>
            {t("duplicate_event")}
          </Dropdown.ItemLink>
        )}
        {canDelete && (
          <Dropdown.ItemLink
            onClick={onDeleteClickHandler}
            id="event-menu-delete"
            forceClose
          >
            <span className="text-v2-danger">{t("delete_event")}</span>
          </Dropdown.ItemLink>
        )}
        {isEventShareableViaBroadcast && (
          <>
            <Dropdown.Separator />
            <Dropdown.ItemLink
              data-testid="send-announcement-event-action"
              disabled={isSendingAnnouncement}
              onClick={() => onSendAnnouncement({ eventId: event.id })}
            >
              <div className="flex items-center justify-between gap-2">
                {t("share_via_broadcast")}
                <BadgeV2 label={t("new")} square />
              </div>
            </Dropdown.ItemLink>
          </>
        )}
        {canDuplicate && !isAllEventsPage && (
          <>
            <Dropdown.Separator />
            <PinEventToTopAction event={event} canEdit={canEdit} />
          </>
        )}
      </Dropdown>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title={
          eventType === EVENT_TYPES.RECURRING
            ? t("delete_event_confirmation_recurring")
            : t("delete_event_confirmation")
        }
        onClose={toggleConfirmationModal}
        onCancel={toggleConfirmationModal}
        onConfirm={() => onDelete("")}
        confirmVariant="danger"
        disabled={isDeleting}
        confirmText={isDeleting ? t("deleting") : t("confirm")}
      >
        {t("delete_event_descriptor")}
      </ConfirmationModal>

      <DuplicateEventModal
        event={event}
        show={isDuplicateEventModalOpen}
        onClose={onDuplicate}
      />
      {!isPaidEvent(event) && (
        <DeleteRecurringModal
          show={isRecurringModalOpen}
          onConfirm={(frequency: RecurringEditMode) => onDelete(frequency)}
          isDeleting={isDeleting}
          onCloseModal={toggleRecurringModal}
        />
      )}
    </>
  );
};
